import { useState, useEffect, useCallback, useMemo } from 'react';
import { ExternalLink, LayoutGrid, Wrench, Server, Search } from 'lucide-react';
import { platformsAPI } from '../utils/api';
import { PageHeader, EmptyState } from '../components/layout/UI';
import { useToast, apiError } from '../components/Toast';

/**
 * frontend/src/pages/PlatformsPage.jsx
 *
 * Read-only directory of the systems and tools the team uses. Everyone sees
 * it; only Admins change it, from Admin → Platforms.
 *
 * A Tool has no environment, so the environment filter only appears once
 * Systems are in view — showing a filter that cannot apply is worse than not
 * showing one.
 */

const ENVIRONMENTS = ['Development', 'UAT', 'Staging', 'Production'];

/**
 * This page is a launcher: people come here to open a Q-Lana system quickly
 * rather than retyping its URL. The one mistake that actually costs something
 * is opening PRODUCTION while meaning to open UAT — on a client's live system.
 *
 * So Production is the loudest thing on the tile, not the calmest. It uses
 * Sunglow, the brand's highlight/warning token. Red is reserved for errors and
 * destructive actions, so it is deliberately not used here: this is "pay
 * attention", not "something is wrong".
 *
 * An earlier version had Production in the quietest colour, and UAT in an
 * amber that is not in the brand palette at all.
 */
const ENV_STYLE = {
  Development: { bg: 'rgba(46,83,159,.10)',  fg: '#2E539F', band: null,      strong: false },
  UAT:         { bg: 'rgba(92,195,188,.16)', fg: '#2f8a84', band: '#5CC3BC', strong: false },
  Staging:     { bg: 'rgba(92,195,188,.16)', fg: '#2f8a84', band: '#5CC3BC', strong: false },
  Production:  { bg: '#FFC849',              fg: '#4a3600', band: '#FFC849', strong: true  },
};

/** Initials for a platform with no logo, so a tile is never blank. */
const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

export default function PlatformsPage() {
  const toast = useToast();
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [category, setCat]  = useState('');      // '' = all
  const [environment, setEnv] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await platformsAPI.getAll({
        category: category || undefined,
        environment: environment || undefined,
        search: search || undefined,
      });
      setItems(r.data || []);
    } catch (e) {
      toast.error(apiError(e, 'Could not load the platform directory.'));
      setItems([]);
    } finally { setLoad(false); }
  }, [category, environment, search, toast]);

  // Debounced while typing; immediate when a filter changes.
  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // Switching away from Systems makes the environment filter meaningless.
  // Either category can carry an environment now, so the filter always applies.

  const grouped = useMemo(() => {
    const external = items.filter(p => p.category === 'External');
    const internal = items.filter(p => p.category === 'Internal');
    return { external, internal };
  }, [items]);

  const Tile = ({ p }) => {
    const env = p.environment ? (ENV_STYLE[p.environment] || ENV_STYLE.Development) : null;
    return (
      <a href={p.link} target="_blank" rel="noopener noreferrer"
        title={`${p.link}${p.description ? ' — ' + p.description : ''}`}
        className="group relative flex flex-col items-center text-center rounded-xl p-4 transition-shadow hover:shadow-md overflow-hidden"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          // A band you notice before you read the name.
          borderTop: env?.band ? `4px solid ${env.band}` : '1px solid var(--border)',
        }}>

        <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: 'var(--text-secondary)' }}>
          <ExternalLink size={13} />
        </span>

        <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden mb-2.5 flex-shrink-0"
          style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>
          {p.logo_url
            ? <img src={p.logo_url} alt="" className="w-full h-full object-contain"
                onError={e => { e.currentTarget.style.display = 'none';
                                e.currentTarget.nextSibling.style.display = 'flex'; }} />
            : null}
          {/* Shown when there is no logo, or the file has gone missing. */}
          <span className="w-full h-full items-center justify-center text-sm font-bold"
            style={{ display: p.logo_url ? 'none' : 'flex', color: '#2E539F' }}>
            {initials(p.name)}
          </span>
        </div>

        <p className="text-sm font-semibold leading-tight line-clamp-2" style={{ color: 'var(--text)' }}>
          {p.name}
        </p>
        {env && (
          <span className={`mt-1.5 px-2 py-0.5 rounded-full font-bold tracking-wide ${env.strong ? 'text-[11px]' : 'text-[10px]'}`}
            style={{ background: env.bg, color: env.fg }}>
            {env.strong ? p.environment.toUpperCase() : p.environment}
          </span>
        )}
        {p.description && (
          <p className="mt-1 text-xs line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
            {p.description}
          </p>
        )}
      </a>
    );
  };

  const Section = ({ title, icon: Icon, list }) => {
    if (!list.length) return null;
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Icon size={15} style={{ color: 'var(--text-secondary)' }} />
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{title}</h2>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{list.length}</span>
        </div>
        <div className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {list.map(p => <Tile key={p.id} p={p} />)}
        </div>
      </div>
    );
  };

  return (
    <div>
      <PageHeader title="Platforms" subtitle="Systems and tools the team uses" />

      <div className="card mb-5 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-48 max-w-xs relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-secondary)' }} />
          <input className="input pl-8" placeholder="Search platforms…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="flex rounded-lg border overflow-hidden text-sm" style={{ borderColor: 'var(--border)' }}>
          {[['', 'All'], ['Internal', 'Internal'], ['External', 'External']].map(([v, l]) => (
            <button key={l} onClick={() => setCat(v)}
              className={`px-3 py-1.5 font-medium transition-colors ${category === v ? 'bg-brand-500 text-white' : ''}`}
              style={category !== v ? { color: 'var(--text-secondary)', background: 'var(--surface)' } : {}}>
              {l}
            </button>
          ))}
        </div>

        <select className="select w-44 text-sm" value={environment} onChange={e => setEnv(e.target.value)}>
          <option value="">All environments</option>
          {ENVIRONMENTS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !items.length ? (
        <EmptyState icon={LayoutGrid} title="No platforms here"
          message={search || category || environment
            ? 'Nothing matches those filters. Try clearing them.'
            : 'An Admin can add platforms under Admin → Platforms.'} />
      ) : (
        <>
          {/* Internal first: it is what people reach for most days. */}
          <Section title="Internal — software we use"      icon={Wrench} list={grouped.internal} />
          <Section title="External — platforms we build"   icon={Server} list={grouped.external} />
        </>
      )}
    </div>
  );
}
